import { CdkObserveContent } from '@angular/cdk/observers';
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { provideBaseClass, useModifier } from '@bynary/composables/class';
import { TranslatePipe } from '@ngx-translate/core';
import { SonaAuthApiFacade } from '@sona/angular-auth-api';
import { SonaAuthUserMenuComponent } from '@sona/angular-auth-feature-user-menu';
import { UpgradeButtonComponent } from '@sona/angular-payments';
import { AppFooterComponent } from '@sona/angular-ui-components/app-footer';
import { IBreadcrumbData } from '@sona/angular-ui-components/breadcrumbs';

const isElementScrollable = (el: HTMLElement) =>
    el.scrollHeight > el.clientHeight;

@Component({
    selector: 'sona-root-layout',
    imports: [
        RouterModule,
        SonaAuthUserMenuComponent,
        CdkObserveContent,
        NgTemplateOutlet,
        AppFooterComponent,
        TranslatePipe,
        UpgradeButtonComponent,
        NgOptimizedImage
    ],
    templateUrl: './root.layout.html',
    styleUrls: ['./root.layout.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [provideBaseClass('c-sona-root-layout')],
    host: {
        class: 'c-sona-root-layout'
    }
})
export class AppRootLayout implements OnInit, OnDestroy {
    readonly homeBreadcrumb = {
        link: '/',
        label: '',
        icon: {
            src: 'assets/images/siteboard-logo-full.svg',
            alt: 'siteboard Logo',
            width: 153,
            height: 24,
            highPriority: true
        }
    } satisfies IBreadcrumbData;

    private readonly _el = inject(ElementRef).nativeElement;
    readonly router = inject(Router);
    readonly isAuthenticated = inject(SonaAuthApiFacade).isAuthenticated;
    readonly isScrollable = useModifier('scrollable', {
        initialValue: isElementScrollable(this._el)
    });
    readonly resizeObserver = new ResizeObserver(() => this.updateScrollable());

    get isHomeRoute() {
        return (
            this.router.isActive('/scan', {
                paths: 'exact',
                matrixParams: 'ignored',
                queryParams: 'ignored',
                fragment: 'ignored'
            }) ||
            this.router.isActive('/domains', {
                paths: 'exact',
                matrixParams: 'ignored',
                queryParams: 'ignored',
                fragment: 'ignored'
            })
        );
    }

    ngOnInit() {
        this.resizeObserver.observe(this._el);
    }

    ngOnDestroy() {
        this.resizeObserver.disconnect();
    }

    updateScrollable() {
        setTimeout(() => {
            this.isScrollable.set(isElementScrollable(this._el));
        }, 300);
    }

    protected readonly Router = Router;
}
